import { useState, useEffect } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { printZpl, setSelectedDevice, clearSelectedDevice } from "@/lib/zebra";
import { usePrinterTarget } from "@/lib/usePrinterTarget";
import { PrintDestination } from "@/components/PrintDestination";
import { printerDisplayName, MEDIA_LABEL } from "@/lib/printerMap";
import { toast } from "sonner";
import { notifyPdfNoStamp } from "@/lib/printNotice";
import { Printer, FileText, QrCode } from "lucide-react";

const VERSIONS = [
  { key: "B", title: "Version B — Hang tag 4×6", desc: "Full detail: Year/Make/Model, VIN, DNR#, QR + barcode. Default for trucks." },
  { key: "A", title: "Version A — Sticker 4×3", desc: "Full detail on an adhesive sticker; uses the whole label face." },
  { key: "C", title: "Version C — Barcode 3×1", desc: "Compact scan-me tag: stock # + QR + barcode only (VIN / Y-M-M don't fit 1\")." },
];

const VERSION_MEDIA = { A: "sticker", B: "hang_tag", C: "barcode_label" };

// Print a real donor/truck DNR tag (QR + Code128 + DNR#) — Zebra via Browser Print,
// with a print-at-100% PDF fallback. The VERSION is the media choice (A=4x3 sticker,
// B=4x6 hang tag, C=3x1 barcode) and each media lives in exactly one printer, so it auto-routes.
export function DonorTagButton({ donorId, stockNumber, compact = false }) {
  const [open, setOpen] = useState(false);
  const [version, setVersion] = useState("B");   // 4x6 hang tag is the safe default for trucks
  const [busy, setBusy] = useState(false);
  const printer = usePrinterTarget(VERSION_MEDIA[version]);

  useEffect(() => { if (open) printer.reload(); }, [open]);   // eslint-disable-line react-hooks/exhaustive-deps

  // One click: the chosen version already determines the media, hence the printer.
  const printZebra = async () => {
    if (busy) return;
    if (!printer.target) {
      toast.error(printer.needsSetup
        ? `No printer is set for ${MEDIA_LABEL[VERSION_MEDIA[version]]} stock. Open Printer Setup and map it — nothing was printed.`
        : "No printer detected — start Zebra Browser Print.");
      return;
    }
    setBusy(true);
    setSelectedDevice(printer.target);
    try {
      const { data } = await api.post(`/donors/${donorId}/tag/zpl`, null, { params: { version } });
      const dev = await printZpl(data.zpl, null, VERSION_MEDIA[version]);   // guard refuses any printer not holding this stock
      // confirm-driven stamp: only reached if the browser accepted the job (no throw/abort)
      await api.post("/donors/tags/label-printed/confirm", { donor_ids: [donorId], version }).catch(() => {});
      toast.success(`Sent ${data.stock_number} tag (Version ${version}) to ${printerDisplayName(dev)}.`);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(detail ? formatApiErrorDetail(detail) : `Zebra Browser Print not reachable — use the PDF fallback. (${e.message})`);
    } finally { clearSelectedDevice(); setBusy(false); }
  };

  const openPdf = async () => {
    setBusy(true);
    try {
      const res = await api.get(`/donors/${donorId}/tag/pdf`, { params: { version }, responseType: "blob" });
      window.open(URL.createObjectURL(res.data), "_blank");
      toast.message("PDF opened.", { description: "Print at Actual Size / 100% — NOT Fit to Page." });
      notifyPdfNoStamp();
    } catch (e) { toast.error("Could not build the tag PDF."); }
    finally { setBusy(false); }
  };

  return (
    <>
      {compact ? (
        <Button data-testid={`donor-row-print-tag-${stockNumber}`} size="sm" variant="outline" onClick={() => setOpen(true)}
          className="rounded-lg h-7 border-slate-200" title="Print truck tag"><QrCode className="w-3.5 h-3.5" /></Button>
      ) : (
        <Button data-testid="donor-detail-print-tag" variant="outline" onClick={() => setOpen(true)}
          className="rounded-lg border-slate-300 h-9"><QrCode className="w-4 h-4 mr-1.5" />Print Tag</Button>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="donor-tag-dialog" className="rounded-xl">
          <DialogHeader>
            <DialogTitle>Print truck tag — {stockNumber}</DialogTitle>
            <DialogDescription>
              QR + barcode + DNR#. Pick a version, then send to the Zebra or open the PDF fallback.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-2">
            {VERSIONS.map((v) => (
              <button key={v.key} data-testid={`donor-tag-version-${v.key}`} onClick={() => setVersion(v.key)}
                className={`w-full text-left rounded-lg border p-3 transition-all ${version === v.key ? "border-[#B01E28] bg-red-50/60 ring-1 ring-[#B01E28]" : "border-slate-200 hover:border-slate-300"}`}>
                <div className="flex items-center gap-2">
                  <span className={`w-4 h-4 rounded-full border flex items-center justify-center ${version === v.key ? "border-[#B01E28]" : "border-slate-300"}`}>
                    {version === v.key && <span className="w-2 h-2 rounded-full bg-[#B01E28]" />}
                  </span>
                  <span className="font-display font-bold text-sm text-slate-800">{v.title}</span>
                </div>
                <div className="text-xs text-slate-500 mt-0.5 ml-6">{v.desc}</div>
              </button>
            ))}
          </div>

          <PrintDestination {...printer} testid="donor-tag-destination" />

          <div className="flex gap-2 pt-1">
            <Button data-testid="donor-tag-print-zebra" disabled={busy} onClick={printZebra}
              className="flex-1 rounded-lg bg-[#E0781A] hover:bg-[#C76714] text-white"><Printer className="w-4 h-4 mr-1.5" />Print to Zebra</Button>
            <Button data-testid="donor-tag-print-pdf" disabled={busy} variant="outline" onClick={openPdf}
              className="flex-1 rounded-lg border-slate-300"><FileText className="w-4 h-4 mr-1.5" />Download PDF</Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
